import {
  DynamoDBClient,
  QueryCommand,
  ScanCommand,
} from "@aws-sdk/client-dynamodb";
import { unmarshall } from "@aws-sdk/util-dynamodb";
import {
  BedrockRuntimeClient,
  InvokeModelCommand,
} from "@aws-sdk/client-bedrock-runtime";
import {
  BedrockAgentRuntimeClient,
  InvokeAgentCommand,
  InvokeInlineAgentCommand,
} from "@aws-sdk/client-bedrock-agent-runtime";
import { createAwsClient } from "./AwsAuth";
import {
  extractBetweenTags,
  removeCharFromStartAndEnd,
  handleFormatter,
} from "./Utils.js";

import {
  AGENT_ID,
  AGENT_ALIAS_ID,
  QUESTION_ANSWERS_TABLE_NAME,
  ASSISTANT_SETTINGS_TABLE_NAME,
  MODEL_ID_FOR_CHART_AND_DASHBOARD,
  CHART_PROMPT,
  DASHBOARD_PROMPT,
} from "../env.js";

/**
 * Query data from DynamoDB
 *
 * @param {string} id - The ID to query
 * @returns {Promise<Object>} - The query response
 */
export const getQueryResults = async (queryUuid = "") => {
  let queryResults = [];
  try {
    const dynamodb = await createAwsClient(DynamoDBClient);

    const input = {
      TableName: QUESTION_ANSWERS_TABLE_NAME,
      KeyConditionExpression: "id = :queryUuid",
      ExpressionAttributeValues: {
        ":queryUuid": {
          S: queryUuid,
        },
      },
      ConsistentRead: true,
    };

    console.log("------- Get Query Results -------");
    console.log(input);

    const command = new QueryCommand(input);
    const response = await dynamodb.send(command);

    if (response.hasOwnProperty("Items")) {
      for (let i = 0; i < response.Items.length; i++) {
        queryResults.push({
          query: response.Items[i].query.S,
          query_results: JSON.parse(response.Items[i].data.S).result,
        });
      }
    }

    return queryResults;
  } catch (error) {
    console.error("Error querying DynamoDB:", error);
    throw error;
  }
};

/**
 * Get assistants from the DynamoDB table
 * @returns {Promise<Array>} - Array of assistant settings objects
 */
export const getAssistants = async () => {
  try {
    const dynamodb = await createAwsClient(DynamoDBClient);

    // Use scan operation to get all items from the table
    const input = {
      TableName: ASSISTANT_SETTINGS_TABLE_NAME,
    };

    console.log("------- Getting Assistants -------");

    const command = new ScanCommand(input);
    const response = await dynamodb.send(command);

    // Transform DynamoDB format to standard JSON
    const assistants = response.Items.map((item) => {
      let formattedItem = unmarshall(item);
      formattedItem.selected_model = 0;
      formattedItem.selected_communication_style = 0;

      formattedItem.schema = new Function(
        `return ${formattedItem["schema"]}`
      )();
      formattedItem.language_questions = new Function(
        `return ${formattedItem["language_questions"]}`
      )();
      formattedItem.models = new Function(
        `return ${formattedItem["models"]}`
      )();
      formattedItem.communication_styles = new Function(
        `return ${formattedItem["communication_styles"]}`
      )();

      return formattedItem;
    });

    return assistants;
  } catch (error) {
    console.error("Error retrieving assistants from DynamoDB:", error);
    throw error;
  }
};

/**
 * Invoke an AWS Bedrock agent
 *
 * @param {string} sessionId - The session ID
 * @param {string} inputText - The text to send to the agent
 * @returns {Promise<Object>} - The agent response with comprehensive data
 */
export const invokeBedrockAgent = async (
  sessionId,
  inputText = "",
  setAnswers,
  setControlAnswers,
  userName = "XXXX",
  //formattedDate = "",
  //formattedTime = "",
  queryUuid = ""
) => {
  try {
    const bedrock = await createAwsClient(BedrockAgentRuntimeClient);

    const input = {
      agentId: AGENT_ID,
      agentAliasId: AGENT_ALIAS_ID,
      sessionId,
      inputText,
      sessionState: {
        promptSessionAttributes: {
          userName: userName,
          //currentDate: formattedDate,
          //currentTime: formattedTime,
          queryUuid: queryUuid,
        },
      },
      enableTrace: true,
    };

    const command = new InvokeAgentCommand(input);
    console.log("------- Invoke Agent -------");
    console.log(command);

    let completion = "";
    let runningTraces = [];
    let countRationals = 0;

    const response = await bedrock.send(command);
    if (response.completion === undefined) {
      throw new Error("Completion is undefined");
    }

    for await (let chunkEvent of response.completion) {
      //console.log("------- Invoke Agent - Chunk Response -------");
      //console.log("Full chunk event structure:", JSON.stringify(chunkEvent));
      if (chunkEvent.chunk) {
        const chunk = chunkEvent.chunk;
        const decodedResponse = new TextDecoder("utf-8").decode(chunk.bytes);
        completion += decodedResponse;
      }

      if (chunkEvent.trace) {
        runningTraces = [...runningTraces, chunkEvent.trace.trace];

        if (chunkEvent.trace.trace.orchestrationTrace?.rationale?.text) {
          console.log("-----rationale------");
          console.log(
            chunkEvent.trace.trace.orchestrationTrace?.rationale?.text
          );
          countRationals++;
          setAnswers((prevState) => [
            ...prevState,
            {
              rationaleText:
                chunkEvent.trace.trace.orchestrationTrace?.rationale?.text,
            },
          ]);
          setControlAnswers((prevState) => [...prevState, {}]);
        }
      }
    }

    // Calculate token usage
    let usage = [];
    let totalInputTokens = 0;
    let totalOutputTokens = 0;

    for (const trace of runningTraces) {
      if (trace.orchestrationTrace?.modelInvocationOutput?.metadata?.usage) {
        usage.push(
          trace.orchestrationTrace?.modelInvocationOutput?.metadata?.usage
        );
        totalInputTokens +=
          trace.orchestrationTrace?.modelInvocationOutput?.metadata?.usage
            .inputTokens;
        totalOutputTokens +=
          trace.orchestrationTrace?.modelInvocationOutput?.metadata?.usage
            .outputTokens;
      }
    }

    console.log("------- Invoke Agent - Traces -------");
    console.log(runningTraces);
    console.log("------- Invoke Agent - Completion -------");
    console.log(completion);
    return {
      sessionId,
      completion,
      usage,
      totalInputTokens,
      totalOutputTokens,
      runningTraces,
      countRationals,
    };
  } catch (error) {
    console.error("Error invoking Bedrock agent:", error);
    throw error;
  }
};

/**
 * Generates a chart based on answer and data
 * @param {Object} answer - Answer object containing text
 * @param {Function} setDashboardEnabled - State setter function for dashboard
 * @returns {Object} Chart configuration or rationale for no chart
 */
export const generateChart = async (answer, setDashboardEnabled) => {
  try {
    const bedrock = await createAwsClient(BedrockRuntimeClient);
    let query_results = "";
    for (let i = 0; i < answer.queryResults.length; i++) {
      query_results +=
        JSON.stringify(answer.queryResults[i].query_results) + "\n";
    }

    // Prepare the prompt
    let new_chart_prompt = CHART_PROMPT.replace(
      /<<answer>>/i,
      answer.text
    ).replace(/<<data_sources>>/i, query_results);

    const payload = {
      anthropic_version: "bedrock-2023-05-31",
      max_tokens: 2000,
      temperature: 1,
      messages: [
        {
          role: "user",
          content: [{ type: "text", text: new_chart_prompt }],
        },
      ],
    };

    // Send the request to Bedrock
    console.log("------- Request chart -------");
    console.log(payload);

    const command = new InvokeModelCommand({
      contentType: "application/json",
      body: JSON.stringify(payload),
      modelId: MODEL_ID_FOR_CHART_AND_DASHBOARD,
    });

    const apiResponse = await bedrock.send(command);
    const decodedResponseBody = new TextDecoder().decode(apiResponse.body);
    const responseBody = JSON.parse(decodedResponseBody).content[0].text;

    console.log("------- Response chart generation -------");
    console.log(responseBody);

    // Process the response
    const has_chart = parseInt(extractBetweenTags(responseBody, "has_chart"));

    if (has_chart) {
      const chartConfig = JSON.parse(
        extractBetweenTags(responseBody, "chart_configuration")
      );
      const chart = {
        chart_type: removeCharFromStartAndEnd(
          extractBetweenTags(responseBody, "chart_type"),
          "\n"
        ),
        chart_configuration: handleFormatter(chartConfig),
        caption: removeCharFromStartAndEnd(
          extractBetweenTags(responseBody, "caption"),
          "\n"
        ),
      };

      console.log("------- Final chart generation -------");
      console.log(chart);

      setDashboardEnabled(true);

      return chart;
    } else {
      return {
        rationale: removeCharFromStartAndEnd(
          extractBetweenTags(responseBody, "rationale"),
          "\n"
        ),
      };
    }
  } catch (error) {
    console.error("Chart generation failed:", error);
    return {
      rationale: "Error generating or parsing chart data.",
    };
  }
};

/**
 * Generates dashboard content based on questions, answers, and chart data
 *
 * @param {Array} questions_and_answers - Array of question and answer pairs
 * @param {Array} data_charts - Array of chart data with captions
 * @returns {Object} Dashboard content with title, executive summary, data analysis, and conclusion
 */
export const generateDashboard = async (questions_and_answers, data_charts) => {
  const bedrock = await createAwsClient(BedrockRuntimeClient);
  let new_dashboard_prompt = DASHBOARD_PROMPT.replace(
    /<<data_charts>>/i,
    JSON.stringify(data_charts)
  );
  new_dashboard_prompt = new_dashboard_prompt.replace(
    /<<questions_and_answers>>/i,
    JSON.stringify(questions_and_answers)
  );

  const payload = {
    anthropic_version: "bedrock-2023-05-31",
    max_tokens: 2000,
    temperature: 1,
    messages: [
      {
        role: "user",
        content: [{ type: "text", text: new_dashboard_prompt }],
      },
    ],
  };

  const command = new InvokeModelCommand({
    contentType: "application/json",
    body: JSON.stringify(payload),
    modelId: MODEL_ID_FOR_CHART_AND_DASHBOARD,
  });

  try {
    const apiResponse = await bedrock.send(command);
    // Decode and return the response(s)
    const decodedResponseBody = new TextDecoder().decode(apiResponse.body);
    /** @type {MessagesResponseBody} */
    const responseBody = JSON.parse(decodedResponseBody).content[0].text;

    const json = {
      title: removeCharFromStartAndEnd(
        extractBetweenTags(responseBody, "title"),
        "\n"
      ),
      executive_summary: removeCharFromStartAndEnd(
        extractBetweenTags(responseBody, "executive_summary"),
        "\n"
      ),
      data_analysis: removeCharFromStartAndEnd(
        extractBetweenTags(responseBody, "data_analysis"),
        "\n"
      ),
      conclusion: removeCharFromStartAndEnd(
        extractBetweenTags(responseBody, "conclusion"),
        "\n"
      ),
    };

    return json;
  } catch (error) {
    console.log("Dashboard generation failed: ", error);
    return {
      title: "Dashboard Generation Error",
      executive_summary: "An error occurred while generating the dashboard.",
      data_analysis: "Unable to analyze data due to an error.",
      conclusion: "Please try again.",
    };
  }
};

/**
 * Invoke an AWS Bedrock inline agent with streaming console output
 *
 * @param {string} sessionId - The session ID
 * @param {string} inputText - The text to send to the agent
 * @param {Function} setAnswers - State setter for answers
 * @param {Function} setControlAnswers - State setter for control answers
 * @param {string} userName - User name
 * @param {string} queryUuid - Query UUID
 * @param {string} agentInstruction - Agent instruction
 * @param {string} agentSchema - Agent schema
 * @param {string} modelId - Model ID
 * @param {string} assistantDbName - Assistant database name
 * @returns {Promise<Object>} - The agent response with comprehensive data
 */
export const invokeBedrockInlineAgent = async (
  sessionId,
  inputText = "",
  setAnswers,
  setControlAnswers,
  userName = "XXXX",
  queryUuid = "",
  agentInstruction = "",
  agentSchema = "",
  modelId = "",
  assistantDbName = ""
) => {
  try {
    const actionGroups = [
      {
        actionGroupName: "executesqlquery",
        description: "Provide agent tools",
        actionGroupExecutor: {
          lambda:
            "arn:aws:lambda:us-east-1:682830732097:function:sam-bedrock-data-analyst--AssistantAPIPostgreSQLHa-W0manwRWOaEp",
        },
        apiSchema: {
          payload: JSON.stringify(agentSchema, null, 2),
        },
      },
    ];

    const bedrock = await createAwsClient(BedrockAgentRuntimeClient);

    const input = {
      agentId: AGENT_ID,
      agentAliasId: AGENT_ALIAS_ID,
      instruction: agentInstruction,
      foundationModel: modelId,
      actionGroups,
      sessionId,
      inputText,
      inlineSessionState: {
        promptSessionAttributes: {
          userName: userName,
          queryUuid: queryUuid,
          assistantDbName: assistantDbName,
        },
      },
      enableTrace: true,
      streamingConfigurations: { streamFinalResponse: true }
    };

    const command = new InvokeInlineAgentCommand(input);
    console.log("------- Invoke Inline Agent -------");
    console.log(command);

    let completion = "";
    let runningTraces = [];
    let countRationals = 0;
    let hasReceivedFirstChunk = false;

    // Initialize streaming output
    console.log("------- Inline Agent Response (Streaming) -------");
    let streamingOutput = "";

    const response = await bedrock.send(command);
    if (response.completion === undefined) {
      throw new Error("Completion is undefined");
    }

    for await (let chunkEvent of response.completion) {
      // Handle text chunks from the agent's response
      if (chunkEvent.chunk) {
        const chunk = chunkEvent.chunk;
        const decodedResponse = new TextDecoder("utf-8").decode(chunk.bytes);

        streamingOutput += decodedResponse;
        completion += decodedResponse;

        // Add initial answer object only after receiving the first chunk
        if (!hasReceivedFirstChunk) {
          hasReceivedFirstChunk = true;
          setAnswers((prevState) => [
            ...prevState,
            { text: completion, isStreaming: true },
          ]);
          setControlAnswers((prevState) => [
            ...prevState,
            { current_tab_view: "answer" },
          ]);
        } else {
          // Update the existing streaming answer with new text
          setAnswers((prevState) => {
            const newState = [...prevState];
            // Find the last answer that is streaming and update it
            for (let i = newState.length - 1; i >= 0; i--) {
              if (newState[i].isStreaming) {
                newState[i] = {
                  ...newState[i],
                  text: completion,
                };
                break;
              }
            }
            return newState;
          });
        }

        // Log with timestamp for debugging
        //console.log(
        //  `[${new Date().toISOString()}] Chunk received:`,
        //  decodedResponse
        //);

      }

      // Handle trace information
      if (chunkEvent.trace) {
        runningTraces = [...runningTraces, chunkEvent.trace.trace];

        if (chunkEvent.trace.trace.orchestrationTrace?.rationale?.text) {
          console.log("\n-----rationale------");
          console.log(
            chunkEvent.trace.trace.orchestrationTrace?.rationale?.text
          );
          countRationals++;
          setAnswers((prevState) => [
            ...prevState,
            {
              rationaleText:
                chunkEvent.trace.trace.orchestrationTrace?.rationale?.text,
            },
          ]);
          setControlAnswers((prevState) => [...prevState, {}]);
        }

        // Log other trace events for debugging
        if (
          chunkEvent.trace.trace.orchestrationTrace?.observation?.finalResponse
            ?.text
        ) {
          console.log("\n-----final response------");
          console.log(
            chunkEvent.trace.trace.orchestrationTrace?.observation
              ?.finalResponse?.text
          );
        }

        if (
          chunkEvent.trace.trace.orchestrationTrace?.invocationInput
            ?.invocationInputs
        ) {
          console.log("\n-----invocation input------");
          console.log(
            JSON.stringify(
              chunkEvent.trace.trace.orchestrationTrace?.invocationInput
                ?.invocationInputs,
              null,
              2
            )
          );
        }
      }

      // Handle other event types
      if (chunkEvent.returnControl) {
        console.log("\n-----return control------");
        console.log(JSON.stringify(chunkEvent.returnControl, null, 2));
      }

      if (chunkEvent.internalServerException) {
        console.error("\n-----internal server exception------");
        console.error(chunkEvent.internalServerException);
      }

      if (chunkEvent.validationException) {
        console.error("\n-----validation exception------");
        console.error(chunkEvent.validationException);
      }
    }

    console.log("------- End of Inline Agent Response -------");
    console.log("Complete Streaming Output:", streamingOutput);

    // Calculate token usage
    let usage = [];
    let totalInputTokens = 0;
    let totalOutputTokens = 0;

    for (const trace of runningTraces) {
      if (trace.orchestrationTrace?.modelInvocationOutput?.metadata?.usage) {
        usage.push(
          trace.orchestrationTrace?.modelInvocationOutput?.metadata?.usage
        );
        totalInputTokens +=
          trace.orchestrationTrace?.modelInvocationOutput?.metadata?.usage
            .inputTokens;
        totalOutputTokens +=
          trace.orchestrationTrace?.modelInvocationOutput?.metadata?.usage
            .outputTokens;
      }
    }

    console.log("------- Invoke Inline Agent - Final Summary -------");
    console.log("Total Input Tokens:", totalInputTokens);
    console.log("Total Output Tokens:", totalOutputTokens);
    console.log("Rationale Count:", countRationals);
    console.log("Traces:");
    console.log(runningTraces);
    console.log("------- Complete Response Text -------");
    console.log(completion);

    return {
      sessionId,
      completion,
      usage,
      totalInputTokens,
      totalOutputTokens,
      runningTraces,
      countRationals,
    };
  } catch (error) {
    console.error("Error invoking Bedrock inline agent:", error);
    throw error;
  }
};